/**
 * Cognitive Load Appendix Slide
 * "Cognitive Load Is Massive"
 * Decision volume, context-switching costs, Tuesday Dinner example, mental health toll
 * Sources: Petts & Carlson 2023, Weeks & Ruppanner 2024/2025, Gloria Mark UC Irvine,
 *          Qatalog/Cornell, APA, Daminger 2019
 */

import React from 'react';
import styled from 'styled-components';
import { colors } from '@config/colors';
import { typography } from '@config/typography';
import { spacing } from '@config/spacing';
import {
  AppendixSlideLayout,
  AppendixContentArea,
  SlideHeader,
  AppendixColumn,
  AppendixSectionLabel,
  AppendixStatBar,
  AppendixStatNumber,
  AppendixStatDescription,
  AppendixStatSource,
  AppendixSourceFooter,
  AccentCalloutBox,
  AccentCalloutTitle,
  AccentCalloutBody,
  AccentCalloutBold,
  DarkInsightBox,
  DarkInsightTitle,
  DarkInsightBody,
  DarkInsightEmphasis,
} from '@components/press/pitch-deck/slide-primitives';

// ============================================
// Decision tree example (unique to this slide)
// ============================================

const DecisionList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px ${spacing.sm};
  margin-top: 3px;
`;

const DecisionItem = styled.div`
  font-family: ${typography.fontFamily.body};
  font-size: clamp(8px, 0.8vw, ${typography.fontSize.xxs});
  color: ${colors.highlight.primary};
  line-height: 1.4;
  padding-left: ${spacing.xs};
  border-left: 2px solid ${colors.accent.yellow.dark};
`;

// ============================================
// Toll grid (unique to this slide)
// ============================================

const TollGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px ${spacing.sm};
`;

// ============================================
// Component
// ============================================

export const CognitiveLoadSlide: React.FC = () => (
  <AppendixSlideLayout>
    <SlideHeader
      title="Cognitive Load Is Massive"
      subtitle="The invisible thinking behind every household decision — fragmented across apps, siloed in one head"
    />

    <AppendixContentArea>
      <AppendixColumn>
        <AppendixSectionLabel>The Cognitive Labor Gap</AppendixSectionLabel>

        <AppendixStatBar>
          <AppendixStatNumber>5+ hrs</AppendixStatNumber>
          <AppendixStatDescription>
            per week of cognitive household labor for mothers vs. ~2 hrs for fathers &mdash;
            even when both work full time. This is <em>thinking</em>, not doing.{' '}
            <AppendixStatSource>(Petts &amp; Carlson, 2023)</AppendixStatSource>
          </AppendixStatDescription>
        </AppendixStatBar>

        <AppendixStatBar>
          <AppendixStatNumber>71%</AppendixStatNumber>
          <AppendixStatDescription>
            of all cognitive household tasks &mdash; anticipating needs, planning,
            researching options, monitoring outcomes &mdash; fall to mothers.{' '}
            <AppendixStatSource>(Weeks &amp; Ruppanner, 2024)</AppendixStatSource>
          </AppendixStatDescription>
        </AppendixStatBar>

        <AppendixStatBar>
          <AppendixStatNumber>79%</AppendixStatNumber>
          <AppendixStatDescription>
            of &ldquo;daily core&rdquo; cognitive tasks handled by mothers: the constant
            mental tracking of schedules, meals, supplies, and appointments.{' '}
            <AppendixStatSource>(Weeks et al., 2025)</AppendixStatSource>
          </AppendixStatDescription>
        </AppendixStatBar>

        <AppendixSectionLabel>Example: &ldquo;What&rsquo;s for Dinner Tuesday?&rdquo;</AppendixSectionLabel>

        <AccentCalloutBox $bg={colors.accent.yellow.extraLight} $accent={colors.accent.yellow.exxtraDark}>
          <AccentCalloutTitle $color={colors.accent.yellow.exxtraDark}>
            One &ldquo;simple&rdquo; question &rarr; 8 micro-decisions, 4&ndash;5 app touches
          </AccentCalloutTitle>
          <AccentCalloutBody>
            A single dinner decision cascades through dietary needs, leftovers inventory,
            grocery availability, budget, kid preferences, schedule conflicts, prep time,
            and cleanup logistics.
          </AccentCalloutBody>
          <DecisionList>
            <DecisionItem>Check family calendar for conflicts</DecisionItem>
            <DecisionItem>Recall allergies &amp; preferences</DecisionItem>
            <DecisionItem>Inventory fridge / pantry</DecisionItem>
            <DecisionItem>Search recipe or meal plan app</DecisionItem>
            <DecisionItem>Check grocery delivery availability</DecisionItem>
            <DecisionItem>Estimate prep time vs. schedule</DecisionItem>
            <DecisionItem>Consider budget constraints</DecisionItem>
            <DecisionItem>Coordinate pickup/activity timing</DecisionItem>
          </DecisionList>
        </AccentCalloutBox>
      </AppendixColumn>

      <AppendixColumn>
        <AppendixSectionLabel>Context-Switching Cost</AppendixSectionLabel>

        <AppendixStatBar $accent={colors.accent.purple.exxtraDark} $bg={`${colors.accent.purple.extraLight}50`}>
          <AppendixStatNumber $color={colors.accent.purple.exxtraDark}>9.5 min</AppendixStatNumber>
          <AppendixStatDescription>
            to recover focus after a single context switch. Household managers
            switch contexts dozens of times per day across unrelated domains.{' '}
            <AppendixStatSource>(Gloria Mark, UC Irvine)</AppendixStatSource>
          </AppendixStatDescription>
        </AppendixStatBar>

        <AppendixStatBar $accent={colors.accent.purple.exxtraDark} $bg={`${colors.accent.purple.extraLight}50`}>
          <AppendixStatNumber $color={colors.accent.purple.exxtraDark}>23 min</AppendixStatNumber>
          <AppendixStatDescription>
            to fully refocus on the original task after an interruption.
            Moms face 50&ndash;100+ interruptions/day at home.{' '}
            <AppendixStatSource>(Gloria Mark, UC Irvine)</AppendixStatSource>
          </AppendixStatDescription>
        </AppendixStatBar>

        <AppendixStatBar $accent={colors.accent.purple.exxtraDark} $bg={`${colors.accent.purple.extraLight}50`}>
          <AppendixStatNumber $color={colors.accent.purple.exxtraDark}>40%</AppendixStatNumber>
          <AppendixStatDescription>
            of productive time lost to context switching across fragmented tools.
            Knowledge workers lose nearly half their day &mdash; household managers
            have it worse with zero integration.{' '}
            <AppendixStatSource>(Qatalog &amp; Cornell, 2021)</AppendixStatSource>
          </AppendixStatDescription>
        </AppendixStatBar>

        <AccentCalloutBox $bg={colors.accent.blue.extraLight} $accent={colors.accent.blue.exxtraDark}>
          <AccentCalloutBody>
            <AccentCalloutBold>~19 app opens/day</AccentCalloutBold> for the average smartphone user
            (APA Stress in America, 2024). Household managers cycle through calendar, grocery,
            messaging, school portal, medical portal, banking, meal planning, and more &mdash;
            each with <AccentCalloutBold>its own login, its own data, its own notifications</AccentCalloutBold>.
          </AccentCalloutBody>
        </AccentCalloutBox>

        <DarkInsightBox>
          <DarkInsightTitle $color={colors.accent.coral.extraDark}>Mental Health Toll</DarkInsightTitle>
          <TollGrid>
            <DarkInsightBody>
              <DarkInsightEmphasis>Depression:</DarkInsightEmphasis> Cognitive load is the strongest
              predictor of maternal depression &mdash; more than physical labor
            </DarkInsightBody>
            <DarkInsightBody>
              <DarkInsightEmphasis>Burnout:</DarkInsightEmphasis> 66% of working mothers report
              burnout symptoms, driven by mental load not hours worked
            </DarkInsightBody>
            <DarkInsightBody>
              <DarkInsightEmphasis>Career impact:</DarkInsightEmphasis> Mothers reduce work hours or
              exit workforce due to cognitive overload, not just childcare gaps
            </DarkInsightBody>
            <DarkInsightBody>
              <DarkInsightEmphasis>Relationship strain:</DarkInsightEmphasis> Perceived unfairness
              in cognitive labor is the top predictor of relationship dissatisfaction
            </DarkInsightBody>
          </TollGrid>
        </DarkInsightBox>
      </AppendixColumn>
    </AppendixContentArea>

    <AppendixSourceFooter>
      Petts &amp; Carlson (2023) &middot; Weeks &amp; Ruppanner (2024, 2025) &middot;
      Gloria Mark, UC Irvine &middot; Qatalog &amp; Cornell University (2021) &middot;
      APA Stress in America (2024) &middot; Daminger (2019)
    </AppendixSourceFooter>
  </AppendixSlideLayout>
);
